import { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaFileAlt, FaTimes, FaUpload, FaEye } from 'react-icons/fa';
import UploadDocumentModal from './UploadDocumentModal';

export default function WorkPlanUploadModal({ isOpen, onClose, onSuccess, workPlan }) {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isViewingDocument, setIsViewingDocument] = useState(false);
  const [userId, setUserId] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      setUserId(user?.id || user?._id);
    } catch (error) {
      console.error('Error getting user:', error);
      toast.error('Session expired. Please log in again.');
    }
  }, []);

  const handleUploadSuccess = (files) => {
    setIsUploadModalOpen(false);
    toast.success(`Work plan uploaded successfully!`);
    if (onSuccess) onSuccess();
    onClose();
  };

  const handleUploadClick = () => {
    if (!userId) {
      toast.error('Please log in to upload documents');
      return;
    }
    setIsUploadModalOpen(true);
  };

  const handleViewDocument = () => {
    setIsViewingDocument(true);
  };

  const handleCloseViewer = () => {
    setIsViewingDocument(false);
  };

  if (!isOpen) return null;

  // Check if workPlan has a document to view
  const hasDocument = workPlan?.documentFile || workPlan?.workPlanFile;
  const documentUrl = workPlan?.documentFile || workPlan?.workPlanFile;
  const isPdf = documentUrl?.toLowerCase().endsWith(".pdf");

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div
            ref={modalRef}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-200 to-orange-300 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FaFileAlt className="w-6 h-6 text-orange-800" />
                  <div>
                    <h2 className="text-xl font-bold text-orange-900">Work Plan Management</h2>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/20 text-orange-800">
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Upload Section */}
              <button
                onClick={handleUploadClick}
                className="w-full border-2 border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500 hover:border-orange-500 hover:text-orange-500 hover:bg-orange-50 transition-all mb-4"
              >
                <FaUpload className="mx-auto h-10 w-10 mb-3" />
                <p className="text-lg font-medium">Upload Your Work Plan</p>
                <p className="text-sm mt-1">PDF, Word, Excel, Images supported</p>
              </button>

              {/* View Document Section - Only show if document exists */}
              {hasDocument && (
                <div className="border-t pt-4">
                  <button
                    onClick={handleViewDocument}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl p-4 flex items-center justify-center space-x-2 transition-colors"
                  >
                    <FaEye className="w-5 h-5" />
                    <span className="font-medium">View Current Work Plan</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isViewingDocument && hasDocument && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[60]">
          <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-4xl max-h-[90vh] flex flex-col relative">
            {/* Header with sticky close button */}
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-800">
                Work Plan Document
              </h2>
              <button
                onClick={handleCloseViewer}
                className="text-gray-600 hover:text-gray-900 text-lg"
              >
                ✕
              </button>
            </div>

            {/* Scrollable content */}
            <div className="p-6 overflow-y-auto">
              {/* Metadata */}
              {workPlan && (
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <p className="text-gray-500">Title</p>
                    <p className="font-medium">{workPlan.title || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <p className="font-medium">{workPlan.status || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Created Date</p>
                    <p className="font-medium">
                      {workPlan.createdAt
                        ? new Date(workPlan.createdAt).toLocaleDateString()
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Created By</p>
                    <p className="font-medium">
                      {workPlan.createdBy?.name || workPlan.createdBy?.email || "N/A"}
                    </p>
                  </div>
                </div>
              )}

              {/* File preview */}
              <div className="border rounded-lg overflow-hidden">
                {isPdf ? (
                  <iframe
                    src={documentUrl}
                    title="Work Plan PDF"
                    className="w-full h-[500px]"
                  />
                ) : (
                  <img
                    src={documentUrl}
                    alt="Work Plan"
                    className="w-full h-[500px] object-contain bg-gray-100"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && userId && (
        <UploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          staffId={userId}
          entityId={userId}
          entityType="Staff"
          onUploadSuccessWithFiles={handleUploadSuccess}
          category="Work Plan"
        />
      )}
    </>
  );
}
